import { workspaceInterface } from '@ardoq/workspace-interface';
import { ArdoqId, Metamodel, PersistedMetamodel } from '@ardoq/api-types';
import { globalFilters as filters } from 'collections/filters';
import { closeOtherWorkspaces } from 'streams/context/ContextActions';
import { workspaceNavigationRequested } from 'streams/workspaces/actions';
import { MetamodelContext } from './types';
import { dispatchAction } from '@ardoq/rxbeach';
import { trackClickStartCreatingNewMetamodel } from './tracking';
import { navigate } from 'router/declarative/streamRouterAdapter/actions';
import { logError } from '@ardoq/logging';
import { SimpleBlockDiagramContentType } from '@ardoq/simple-block-diagram/types';
import { TrackingLocation } from '@ardoq/data-model';
import { clearLoadedGraphState } from 'streams/traversals/actions';
import { toInventoryLocation } from 'inventory/inventoryLocation$';
import { InventoryModule } from 'inventory/consts';
import { inventoryTracking } from 'inventory/inventoryTracking';

export const getWorkspaceIdsFromContext = (
  metamodel: Metamodel,
  context: MetamodelContext
) => {
  const { componentTypes, referenceTypes, workspaces } = metamodel.metamodel;
  const {
    COMPONENT,
    REFERENCE,
    REFERENCE_COMPLETENESS,
    WORKSPACE,
    WORKSPACE_FOLDER,
  } = SimpleBlockDiagramContentType;
  if (context.type === COMPONENT) {
    return [componentTypes[context.id].workspaceId];
  } else if (context.type === REFERENCE) {
    return [referenceTypes[context.id].workspaceId];
  } else if (context.type === REFERENCE_COMPLETENESS) {
    return [componentTypes[context.componentId].workspaceId];
  } else if (context.type === WORKSPACE) {
    return [context.id];
  } else if (context.type === WORKSPACE_FOLDER) {
    return Object.values(workspaces)
      .filter(workspace => workspace.folder === context.id)
      .map(workspace => workspace.id);
  }
  return Object.keys(workspaces);
};

const handleOpenWorkspaces = (
  workspaceIds: ArdoqId[],
  isViewpointMode: boolean,
  trackingLocation?: TrackingLocation
) => {
  const selectedWorkspaces: ArdoqId[] = workspaceIds.filter(
    workspaceInterface.workspaceExists
  );
  if (!selectedWorkspaces.length) return;
  filters.removeAllFilters({});
  dispatchAction(
    closeOtherWorkspaces({ keepWorkspaceIds: selectedWorkspaces })
  );
  if (isViewpointMode) {
    dispatchAction(
      clearLoadedGraphState({
        keepCurrentViewPointMode: false,
      })
    );
  }
  selectedWorkspaces.forEach(workspaceId => {
    dispatchAction(
      workspaceNavigationRequested({ workspaceId, trackingLocation })
    );
  });
};

export const handleNavigation = (
  metamodel: Metamodel,
  context: MetamodelContext,
  isViewpointMode: boolean
) => {
  const workspaceIds: string[] = getWorkspaceIdsFromContext(metamodel, context);
  if (!workspaceIds.length) return;
  handleOpenWorkspaces(workspaceIds, isViewpointMode, 'metamodel');
};

export const handleNavigationToInventory = (
  metamodel: Metamodel,
  context: MetamodelContext
) => {
  const workspaceIds = getWorkspaceIdsFromContext(metamodel, context).filter(
    workspaceInterface.workspaceExists
  );
  if (!workspaceIds.length) return;
  inventoryTracking.trackOpenInventoryFromMetamodel();
  dispatchAction(
    navigate(
      toInventoryLocation({
        inventoryModule: InventoryModule.BROWSE,
        selectedWorkspaceIds: workspaceIds,
        dataSourceType: 'workspace',
        useCase: 'pageManageData',
        filterSubquery: null,
      })
    )
  );
};

export const openNewEditor = () => {
  dispatchAction(navigate({ route: '/metamodel/new', params: {} }));
  trackClickStartCreatingNewMetamodel();
};

export const getComponentTypeCount = (metamodel?: PersistedMetamodel) => {
  if (!metamodel?.metamodel) return 0;
  if (!metamodel.metamodel.componentTypes) {
    logError(
      Error('Invalid metamodel: Missing componentTypes property.'),
      null,
      { id: metamodel._id }
    );
  }
  return Object.keys(metamodel.metamodel.componentTypes).length;
};

const LARGE_METAMODEL_COMPONENT_TYPE_COUNT = 200;

export const isLargeMetamodel = (
  metamodel: PersistedMetamodel | null
): boolean =>
  getComponentTypeCount(metamodel ?? undefined) >
  LARGE_METAMODEL_COMPONENT_TYPE_COUNT;
